import { useEffect, useRef, useState } from 'react';
import { HomeSwitch } from './HomeSwitch';
import { IconCheck, IconCopy } from './icons';
import { RecentSearchesDropdown } from './RecentSearchesDropdown';
import { useUser } from '../providers/UserProvider';
import type { BetaSearch } from '../repositories/betaSearchRepository';
import { listRecentSearches, type RecentSearch } from '../repositories/savedSearchRepository';
import { deduplicateRecentSearches } from '../utils/recentSearches';
import { isSearchSubmitShortcut } from '../utils/keyboard';

/*
 * A saved feed's prompt controls on its page (/feeds/<id>): the Home
 * membership switch, the prompt shown in the header's search-bar slot, and the
 * prompt editor it discloses.
 */

/**
 * The Home membership switch. It holds the controls row's left slot, where
 * Home shows its feed chips; the brand's "TLDR" link is the way home.
 */
export function SharedFeedAddAction({ search, isLoggedIn, busy, onAdd }: {
  search: BetaSearch; isLoggedIn: boolean; busy: boolean; onAdd: () => void;
}) {
  if (!isLoggedIn) return null;
  return <label className="reader-shared-home">Show in Home<HomeSwitch on={search.included} disabled={busy} onChange={onAdd} /></label>;
}

/**
 * The feed's prompt, drawn in the header's search-bar slot so a feed page and
 * Home put their query in the same place. It is the disclosure for the prompt
 * editor below the header.
 */
export function FeedPromptBar({ prompt, open, onToggle }: { prompt: string; open: boolean; onToggle: () => void }) {
  return <button type="button" className="beta-search-bar reader-feed-prompt-bar" aria-expanded={open}
    aria-label={open ? 'Hide search prompt' : 'Edit search prompt'} title={prompt} onClick={onToggle}>
    <svg aria-hidden="true" className="beta-search-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="10" cy="10" r="7" /><path d="m15 15 6 6" /></svg>
    <span className="reader-feed-prompt-text">{prompt}</span>
  </button>;
}

export function SharedFeedPromptPanel({
  search,
  open,
  onClose,
  onRunSearch,
  onSave,
  onPreview,
  busy,
  isLoggedIn,
  canSave,
  canPreview = false,
}: {
  search: BetaSearch;
  open: boolean;
  onClose: () => void;
  onRunSearch: (prompt: string) => void;
  onSave: (prompt: string) => void;
  /** Admins judge a draft off to the side before promoting it (PRO-584). */
  onPreview?: (prompt: string) => void;
  busy: boolean;
  isLoggedIn: boolean;
  canSave: boolean;
  canPreview?: boolean;
}) {
  const [promptText, setPromptText] = useState(search.description);
  const [copied, setCopied] = useState(false);
  const { user } = useUser();
  const historyOwner = isLoggedIn ? user?.username : undefined;
  const [history, setHistory] = useState<{ owner: string; items: RecentSearch[] } | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyQuery, setHistoryQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const recentSearches = history?.owner === historyOwner ? history?.items ?? [] : [];
  const historyItems = deduplicateRecentSearches(recentSearches, 5, historyQuery);
  const historyOpen = showHistory && historyItems.length > 0;

  useEffect(() => {
    setHistory(null);
    setShowHistory(false);
    if (!historyOwner || !open) return;
    let cancelled = false;
    listRecentSearches().then(items => {
      if (!cancelled) setHistory({ owner: historyOwner, items });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [historyOwner, open]);

  const selectRecentSearch = (item: RecentSearch) => {
    setShowHistory(false);
    setHighlightedIndex(-1);
    setPromptText(item.description);
    textareaRef.current?.focus();
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setPromptText(search.description);
  }, [search.description]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [promptText, open]);

  if (!open) return null;

  const isDirty = promptText.trim() !== search.description.trim();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const handleDiscard = () => {
    setPromptText(search.description);
  };

  const handleSave = () => {
    if (!isDirty || busy || promptText.trim().length <= 1) return;
    onSave(promptText.trim());
  };

  return (
    <div className="beta-feed-prompt-panel" role="region" aria-label="Search prompt details">
      <div className="beta-feed-prompt-header">
        <span className="beta-feed-prompt-title">Search prompt</span>
        <div className="beta-feed-prompt-actions">
          {recentSearches.length > 0 && <button
            type="button"
            className="beta-feed-prompt-action-btn"
            aria-label="Search history"
            aria-expanded={historyOpen}
            onMouseDown={event => event.preventDefault()}
            onClick={() => {
              setHistoryQuery('');
              setShowHistory(value => !value);
              setHighlightedIndex(-1);
              textareaRef.current?.focus();
            }}
          >History</button>}
          <button
            type="button"
            className={`beta-feed-prompt-action-btn beta-feed-prompt-copy-btn${copied ? ' copied' : ''}`}
            onClick={() => void handleCopy()}
            title="Copy prompt text"
          >
            {copied ? <><IconCheck size={13} /> Copied</> : <><IconCopy size={13} /> Copy</>}
          </button>
          <button
            type="button"
            className="beta-feed-prompt-action-btn beta-feed-prompt-close-btn"
            onClick={onClose}
            title="Close prompt"
            aria-label="Close prompt"
          >
            Close
          </button>
        </div>
      </div>
      <div className="beta-feed-prompt-input">
        <textarea
          ref={textareaRef}
          className="beta-feed-prompt-textarea"
          value={promptText}
          onChange={(e) => {
            setPromptText(e.target.value);
            const query = e.target.value.trim();
            setHistoryQuery(query.length >= 3 ? query : '');
            if (query.length >= 3) setShowHistory(true);
            setHighlightedIndex(-1);
          }}
          onBlur={() => { setShowHistory(false); setHighlightedIndex(-1); }}
          role="combobox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={historyOpen}
          aria-controls={historyOpen ? 'feed-recent-searches' : undefined}
          aria-activedescendant={historyOpen && highlightedIndex >= 0 ? `feed-recent-searches-${highlightedIndex}` : undefined}
          aria-label="Edit search prompt"
          placeholder="Search prompt"
          rows={1}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (historyOpen) {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex(index => e.key === 'ArrowDown'
                  ? (index + 1) % historyItems.length
                  : (index <= 0 ? historyItems.length - 1 : index - 1));
                return;
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                setShowHistory(false);
                setHighlightedIndex(-1);
                return;
              }
              if (e.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < historyItems.length) {
                e.preventDefault();
                selectRecentSearch(historyItems[highlightedIndex]);
                return;
              }
            }
            if (isSearchSubmitShortcut(e)) {
              e.preventDefault();
              if (promptText.trim().length > 1 && !busy) {
                onRunSearch(promptText.trim());
              }
            }
          }}
        />
        <RecentSearchesDropdown
          id="feed-recent-searches"
          searches={historyItems}
          isOpen={historyOpen}
          highlightedIndex={highlightedIndex}
          query={historyQuery}
          onSelect={selectRecentSearch}
        />
      </div>
      <div className="beta-feed-prompt-footer">
        <div className="beta-feed-prompt-footer-right">
          {canSave && (
            <>
              <button
                type="button"
                className="beta-table-btn"
                disabled={busy || !isDirty}
                onClick={handleDiscard}
              >
                Discard changes
              </button>
              <button
                type="button"
                className="beta-table-btn beta-primary-btn"
                disabled={busy || !isDirty || promptText.trim().length <= 1}
                onClick={handleSave}
              >
                {busy ? 'Saving…' : 'Save changes'}
              </button>
            </>
          )}
          {canPreview && onPreview && (
            <button
              type="button"
              className="beta-table-btn"
              title={isDirty
                ? "Judge this draft's top stories without changing the feed"
                : 'Re-judge the live description under the active prompt and judge, without changing the feed'}
              disabled={busy || promptText.trim().length <= 1}
              onClick={() => onPreview(promptText.trim())}
            >
              Preview top stories
            </button>
          )}
          <button
            type="button"
            className="beta-feed-prompt-run-btn"
            disabled={busy || promptText.trim().length <= 1}
            onClick={() => onRunSearch(promptText.trim())}
          >
            {busy ? 'Searching…' : 'Run search →'}
          </button>
        </div>
      </div>
    </div>
  );
}
